import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Aviso } from "../../../../../store/admin/avisosStore";
import ImagePreviewModal from "../../../../../components/base/ImagePreviewModal";
import type { ThemeClasses } from "./types";

interface AvisoCardProps {
  aviso: Aviso;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  companyName?: string;
  isToggling?: boolean;
  onViewDetails: (aviso: Aviso) => void;
  onEdit: (aviso: Aviso) => void;
  onToggleStatus: (aviso: Aviso) => void;
  onDelete: (aviso: Aviso) => void;
}

export default function AvisoCard({
  aviso,
  isDarkMode,
  themeClasses,
  companyName,
  isToggling = false,
  onViewDetails,
  onEdit,
  onToggleStatus,
  onDelete,
}: AvisoCardProps) {
  const { t } = useTranslation("adminSections");
  const [previewOpen, setPreviewOpen] = useState(false);

  const readedCount = Array.isArray(aviso.readed_by)
    ? aviso.readed_by.length
    : 0;

  const fallbackTitle = t("avisos.title");
  const scopeCompanyName = companyName || aviso.company_id || "";

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden hover:shadow-lg transition-all flex flex-col`}
    >
      {aviso.image ? (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="relative group w-full h-40 overflow-hidden focus:outline-none"
          aria-label={t("avisos.form.enlargeImage")}
        >
          <img
            src={aviso.image}
            alt={aviso.title || fallbackTitle}
            className="w-full h-40 object-cover transition-transform group-hover:scale-105"
          />
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
            <i className="ri-zoom-in-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
          </span>
        </button>
      ) : (
        <div
          className={`w-full h-40 flex items-center justify-center ${
            isDarkMode ? "bg-gray-800" : "bg-gray-100"
          }`}
        >
          <i
            className={`ri-megaphone-line text-4xl ${themeClasses.textSecondary}`}
          ></i>
        </div>
      )}

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-2 gap-2">
          <h3
            className={`font-semibold ${themeClasses.text} text-base line-clamp-2`}
          >
            {aviso.title || t("avisos.noTitle")}
          </h3>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
              aviso.status === "active"
                ? "text-primary-500 bg-primary-500/10"
                : "text-gray-500 bg-gray-500/10"
            }`}
          >
            {aviso.status === "active"
              ? t("avisos.active")
              : t("avisos.inactive")}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-2 text-xs flex-wrap">
          {aviso.company_id ? (
            <span
              className="px-2 py-0.5 rounded-full font-medium text-blue-500 bg-blue-500/10"
              title={t("avisos.scopeCompanyLabel", {
                name: scopeCompanyName,
              })}
            >
              <i className="ri-building-line mr-1"></i>
              {companyName || aviso.company_id}
            </span>
          ) : (
            <span
              className="px-2 py-0.5 rounded-full font-medium text-purple-500 bg-purple-500/10"
              title={t("avisos.scopeGlobalLabel")}
            >
              <i className="ri-global-line mr-1"></i>
              {t("avisos.allCompanies")}
            </span>
          )}
          {readedCount > 0 && (
            <span
              className={`px-2 py-0.5 rounded-full ${themeClasses.textSecondary}`}
              title={t("avisos.readedByTitle")}
            >
              <i className="ri-eye-line mr-1"></i>
              {readedCount}
            </span>
          )}
        </div>

        {aviso.description && (
          <p
            className={`text-sm ${themeClasses.textSecondary} line-clamp-3 mb-4`}
          >
            {aviso.description}
          </p>
        )}

        <div className="flex space-x-2 mt-auto pt-3">
          <button
            onClick={() => onViewDetails(aviso)}
            className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} text-sm font-medium transition-colors`}
          >
            <i className="ri-information-line mr-1"></i>
            {t("avisos.viewDetails")}
          </button>
          <button
            onClick={() => onEdit(aviso)}
            className={`py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} text-sm transition-colors`}
            title={t("avisos.edit")}
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            onClick={() => onToggleStatus(aviso)}
            disabled={isToggling}
            className={`py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            title={
              aviso.status === "active"
                ? t("avisos.deactivate")
                : t("avisos.activate")
            }
          >
            {isToggling ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i
                className={
                  aviso.status === "active"
                    ? "ri-eye-off-line"
                    : "ri-eye-line"
                }
              ></i>
            )}
          </button>
          <button
            onClick={() => onDelete(aviso)}
            className="py-2 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-sm transition-colors"
            title={t("avisos.delete")}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      </div>

      {previewOpen && aviso.image && (
        <ImagePreviewModal
          src={aviso.image}
          alt={aviso.title || fallbackTitle}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
